import React from "react";
import { Row, Col } from "react-bootstrap";
import { RBSModal } from "../../base/rbs_components/RBSModal.tsx";
import { RBSTextField } from "../../base/rbs_components/RBSTextField.tsx";
import { RBSDropdown } from "../../base/rbs_components/RBSDropdown.tsx";
import type { SyntheticExcessSpreadInput, SyntheticExcessSpread, WaterfallItemInput, WaterfallItem, Variable } from "../../base/ui_general/GeneralInterfaces.tsx";
import { WaterfallItemTypeEnum, LabelTextMessageTypes, ValidationSchemes, TextfieldInputTypes } from "../../base/Settings.tsx";
import { getLabelTextMessage } from "../../base/utils/GeneralUtilities.tsx";
import type { InputStateControl } from "../../base/utils/InputStateControl.tsx";
import type { UserLanguages } from "../../base/Settings.tsx";

/**
 * Props for SyntheticExcessSpreadDetailModal.
 * This interface describes the data structure for displaying and editing a Synthetic Excess Spread in a modal dialog.
 */
interface SyntheticExcessSpreadDetailModalProps {
    /** Indicates whether the modal is visible. */
    show: boolean;
    /** The SyntheticExcessSpread to display or edit (main type, not input). */
    syntheticExcessSpread?: SyntheticExcessSpread;
    /** The associated WaterfallItem to display or edit. */
    waterfall_item?: WaterfallItem;
    /** Callback to submit the synthetic excess spread input. */
    onSubmit: (syntheticExcessSpreadInput: SyntheticExcessSpreadInput, waterfallItemInput: WaterfallItemInput) => void;
    /** Callback to close the modal. */
    onClose: () => void;
    /** Indicates if the form input is valid. */
    isValid: boolean;
    /** InputStateControl instance for managing input state and validation. */
    inputStateControl: InputStateControl;
    /** Indicates the preferred user language. */
    user_language: UserLanguages;
    /** Array of available variables for reference_variable_id selection. */
    variables: Array<Variable> | undefined;
}

export const SyntheticExcessSpreadDetailModal: React.FC<SyntheticExcessSpreadDetailModalProps> = (props: SyntheticExcessSpreadDetailModalProps) => {
    // Helper to prefill values from syntheticExcessSpread or waterfall_item, or use defaults
    const prefill = (key: string): string => {
        if (props.syntheticExcessSpread && key in props.syntheticExcessSpread) {
            const value = props.syntheticExcessSpread[key as keyof typeof props.syntheticExcessSpread];
            if (key === "invested") return value ? "true" : "false";
            return String(value ?? "");
        }
        if (props.waterfall_item && key in props.waterfall_item) {
            const value = props.waterfall_item[key as keyof typeof props.waterfall_item];
            if (key === "invested") return value ? "true" : "false";
            return String(value ?? "");
        }
        switch (key) {
            case "reference_variable_id":
                return "";
            case "name":
                return "New Synthetic Excess Spread";
            case "description":
                return "Synthetic excess spread for transaction";
            case "excess_spread":
                return "0.005";
            case "invested":
                return "false";
            default:
                return "";
        }
    };
    const headerTitle = props.syntheticExcessSpread
        ? getLabelTextMessage(LabelTextMessageTypes.UPDATE_SYNTHETIC_EXCESS_SPREAD_TITLE, props.user_language)
        : getLabelTextMessage(LabelTextMessageTypes.NEW_SYNTHETIC_EXCESS_SPREAD_CREATION_TITLE, props.user_language);
    return (
        <RBSModal
            show={props.show}
            header_title={headerTitle}
            show_close_button
            onClose={props.onClose}
            primary_button_label={getLabelTextMessage(LabelTextMessageTypes.SUBMIT, props.user_language)}
            secondary_button_label={getLabelTextMessage(LabelTextMessageTypes.CANCEL, props.user_language)}
            onClickSecondary={props.onClose}
            primary_button_disabled={!props.isValid}
            size={"xl"}
            onClickPrimary={() => {
                let transactionIdRaw = props.inputStateControl.getStringValueForKey("transaction_id");
                if (!transactionIdRaw) transactionIdRaw = "";
                const syntheticExcessSpreadInput: SyntheticExcessSpreadInput = {
                    excess_spread: Number(props.inputStateControl.getStringValueForKey("excess_spread")),
                    reference_variable_id: props.inputStateControl.getStringValueForKey("reference_variable_id") ?? ""
                };
                const waterfallItemInput: WaterfallItemInput = {
                    name: props.inputStateControl.getStringValueForKey("name") ?? "",
                    description: props.inputStateControl.getStringValueForKey("description") ?? "",
                    transaction_id: transactionIdRaw,
                    type: WaterfallItemTypeEnum.SYNTHETIC_EXCESS_SPREAD,
                };
                props.onSubmit(syntheticExcessSpreadInput, waterfallItemInput);
                props.onClose();
            }}
        >
            <Row>
                <Col md={6}>
                    <RBSTextField
                        label={getLabelTextMessage(LabelTextMessageTypes.NAME, props.user_language)}
                        input_key="name"
                        prefill={prefill("name")}
                        validation_schemes={[ValidationSchemes.REQUIRED]}
                        input_state_control={props.inputStateControl}
                        type={TextfieldInputTypes.TEXT}
                    />
                </Col>
                <Col md={6}>
                    <RBSTextField
                        label={getLabelTextMessage(LabelTextMessageTypes.EXCESS_SPREAD, props.user_language)}
                        input_key="excess_spread"
                        prefill={prefill("excess_spread")}
                        validation_schemes={[ValidationSchemes.REQUIRED, ValidationSchemes.NUMBER, ValidationSchemes.NON_NEGATIVE]}
                        input_state_control={props.inputStateControl}
                        type={TextfieldInputTypes.NUMBER}
                    />
                </Col>
            </Row>
            <Row>
                <Col md={6}>
                    <RBSDropdown
                        label={getLabelTextMessage(LabelTextMessageTypes.REFERENCE_VARIABLE_ID, props.user_language)}
                        input_key="reference_variable_id"
                        prefill={prefill("reference_variable_id")}
                        items={[{ label: "---", value: "" }, ...(props.variables ? props.variables.map(v => ({ label: v.name, value: v.id })) : [])]}
                        input_state_control={props.inputStateControl}
                        validation_schemes={[ValidationSchemes.REQUIRED]}
                    />
                </Col>
                <Col md={6}>
                    <RBSDropdown
                        label={getLabelTextMessage(LabelTextMessageTypes.INVESTED, props.user_language)}
                        input_key="invested"
                        prefill={prefill("invested")}
                        items={[{ label: "Yes", value: "true" }, { label: "No", value: "false" }]}
                        input_state_control={props.inputStateControl}
                        validation_schemes={[ValidationSchemes.REQUIRED]}
                    />
                </Col>
            </Row>
            <Row>
                <Col md={12}>
                    <RBSTextField
                        label={getLabelTextMessage(LabelTextMessageTypes.DESCRIPTION, props.user_language)}
                        input_key="description"
                        prefill={prefill("description")}
                        validation_schemes={[ValidationSchemes.REQUIRED]}
                        input_state_control={props.inputStateControl}
                        type={TextfieldInputTypes.TEXT}
                        multiline={true}
                        style={{ minHeight: 300 }}
                    />
                </Col>
            </Row>
        </RBSModal>
    );
};
